import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, OutlinedInput, Stack, Tooltip } from '@mui/material';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import MyAutocompleteComponent from '../../pages/FeedbackCollation/CustomAutoComplete';
import { handleColumnsChange } from '../../redux/FeedbackCollation/feedbackCollationSlice';
import { handleDecisionMakingColumnsChange } from '../../redux/DecisionCentral/decisionCentralRulesSlice';
import './table.scss';

function throttle(callback, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      callback(...args);
    }
  };
}

export const DrawerColumns = (props) => {
  const dispatch = useDispatch();
  const { column, row, tableName } = props;
  const {
    recommendation,
    comment,
    taggedUsers,
    agreeDisagreeStatus,
    CycleSKUKey,
    decision,
    completionStatus,
    instructionComment,
    ownerEmail,
    finalComment
  } = row;
  const cellRef = useRef(null);
  const [showmultiplerows, setShowmultiplerows] = useState(false);
  const [resizeInfo, setResizeInfo] = useState(null);
  const [openTooltip, setOpenTooltip] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [commentValue, setCommentValue] = useState(
    tableName === 'Decision Table'
      ? instructionComment
      : tableName === 'Completion Table' && finalComment == null
        ? instructionComment
        : tableName === 'Completion Table'
          ? finalComment
          : comment
  );
  const [recommendationValue, setRecommendationValue] = useState(
    tableName === 'Decision Table' ? decision : recommendation
  );
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const feedbackCollation = useSelector((state) => state.feedbackCollation);
  const { tagUsers } = feedbackCollation;
  const decisionCentralRules = useSelector((state) => state.decisionMakingCentralRules);
  const { tagDecisionUsers } = decisionCentralRules;

  useEffect(() => {
    const resizeObserver = new ResizeObserver(
      throttle((entries) => {
        entries.forEach((entry) => {
          setResizeInfo(entry.contentRect);
        });
      }, 200)
    );

    if (cellRef.current) {
      resizeObserver.observe(cellRef.current);
    }

    return () => {
      if (cellRef.current) {
        resizeObserver.unobserve(cellRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setCommentValue(
     tableName === 'Decision Table'
      ? instructionComment
      : tableName === 'Completion Table' && finalComment == null
        ? instructionComment
        : tableName === 'Completion Table'
          ? finalComment
          : comment
    );
    setRecommendationValue(tableName === 'Decision Table' ? decision : recommendation);
  }, [comment, instructionComment, finalComment, recommendation, decision]);
  const getUserDetails = (users, value) => {
    const user = users?.filter((item) => item?.UserEmail?.toLowerCase() === value?.toLowerCase());
    return user;
  };
  const handleChange = (event, newValue, CycleSKUKey) => {
    setSelectedOptions(newValue);
    if (tableName === 'Decision Table') {
      dispatch(
        handleDecisionMakingColumnsChange({
          value: newValue[newValue.length - 1]?.UserEmail,
          key: 'ownerEmail',
          id: CycleSKUKey
        })
      );
      dispatch(
        handleDecisionMakingColumnsChange({
          value: newValue[newValue.length - 1]?.UserName,
          key: 'ownerName',
          id: CycleSKUKey
        })
      );
    } else {
      dispatch(
        handleColumnsChange({
          value: newValue,
          key: 'taggedUsers',
          id: CycleSKUKey
        })
      );
    }
  };

  const handleClear = () => {
    setSelectedOptions([]);
    if (tableName === 'Decision Table') {
      dispatch(
        handleDecisionMakingColumnsChange({
          value: '',
          key: 'ownerEmail',
          id: CycleSKUKey
        })
      );
      dispatch(
        handleDecisionMakingColumnsChange({
          value: '',
          key: 'ownerName',
          id: CycleSKUKey
        })
      );
    }
  };

  const handleInput = (event, newValue) => {
    setInputValue(newValue);
    const users =
      tableName === 'Decision Table' || tableName === 'Completion Table'
        ? tagDecisionUsers
        : tagUsers;
    setOpen(
      newValue.length > 0 &&
        users.some((option) => option.UserName.toLowerCase().includes(newValue.toLowerCase()))
    );
  };

  const handleDateChange = (newValue, column) => {
    dispatch(
      handleDecisionMakingColumnsChange({
        value: new Date(newValue).toLocaleDateString('en-CA'),
        key: column,
        id: CycleSKUKey
      })
    );
  };

  const setTableCommentValue = (value, key) => {
    if (tableName === 'Decision Table' || tableName === 'Completion Table') {
      dispatch(
        handleDecisionMakingColumnsChange({
          value: value,
          key: key,
          id: CycleSKUKey
        })
      );
    } else {
      dispatch(
        handleColumnsChange({
          value: value,
          key: key,
          id: CycleSKUKey
        })
      );
    }
    setShowmultiplerows(false);
  };

  const handleStatusChange = (value, key) => {
    if (key === 'agreeDisagreeStatus') {
      dispatch(
        handleColumnsChange({
          value,
          key,
          id: CycleSKUKey
        })
      );
    } else if (key === 'completionStatus') {
      dispatch(
        handleDecisionMakingColumnsChange({
          value,
          key,
          id: CycleSKUKey
        })
      );
    }
  };

  return column?.id === 'agreeDisagreeStatus' || column?.id === 'completionStatus' ? (
    <Stack justifyContent="space-between" direction={'row'}>
      <Box
        sx={{
          width: '100px',
          height: '32px',
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-around',
          marginLeft: '10px'
        }}>
        {(column?.id === 'agreeDisagreeStatus' && agreeDisagreeStatus === null) ||
        (column?.id === 'completionStatus' && completionStatus === null) ? (
          <>
            <ThumbUpOffAltIcon
              style={{ width: '1.2em', height: '1.2em', color: '#005CBC' }}
              onClick={() => handleStatusChange(true, column.id)}
            />
            <ThumbDownOffAltIcon
              style={{
                width: '1.2em',
                height: '1.2em',
                color: '#005CBC'
              }}
              onClick={() => handleStatusChange(false, column.id)}
            />
          </>
        ) : (column?.id === 'agreeDisagreeStatus' && agreeDisagreeStatus) ||
          (column?.id === 'completionStatus' && completionStatus) ? (
          <>
            <ThumbUpIcon
              style={{ width: '1.2em', height: '1.2em', color: '#005CBC' }}
              onClick={() => handleStatusChange(true, column.id)}
            />
            <ThumbDownOffAltIcon
              style={{
                width: '1.2em',
                height: '1.2em',
                color: '#005CBC'
              }}
              onClick={() => handleStatusChange(false, column.id)}
            />
          </>
        ) : (
          <>
            <ThumbUpOffAltIcon
              style={{ width: '1.2em', height: '1.2em', color: '#005CBC' }}
              onClick={() => handleStatusChange(true, column.id)}
            />
            <ThumbDownIcon
              style={{
                width: '1.2em',
                height: '1.2em',
                color: '#005CBC'
              }}
              onClick={() => handleStatusChange(false, column.id)}
            />
          </>
        )}
      </Box>
    </Stack>
  ) : column.id === 'action reco' ||
    (column.id === 'decision' && tableName === 'Completion Table') ||
    column.id === 'steering team recommendation' ? (
    <Tooltip
      open={openTooltip === column.id}
      title={<div className="action-name-tooltip">{row[column.id]}</div>}
      onOpen={() => {
        if (row[column.id]?.length > 25) setOpenTooltip(column.id);
      }}
      onClose={() => setOpenTooltip(null)}
      placement="top">
      <div
        style={{
          width: column.width,
          minWidth: column.width,
          fontSize: '14px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
        {row[column.id]}
      </div>
    </Tooltip>
  ) : column.id === 'recommendation' ? (
    <OutlinedInput
      className="text-area-cell"
      onBlur={() => {
        setTableCommentValue(recommendationValue, column.id);
      }}
      onChange={(e) => {
        setRecommendationValue(e.target.value?.replace(/[^a-zA-Z0-9 ]/g, ''));
      }}
      value={recommendationValue || ''}
      inputProps={{
        maxLength: 24
      }}
      placeholder={'Input recommendation'}
      style={{
        width: column.width,
        minWidth: column.width,
        fontSize: '14px',
        padding: '6px 10px',
        borderColor: '1px solid #DFDFDF',
        background: '#fff',
        height: '32px'
      }}
    />
  ) : column.id === 'comment' ||
    column.id === 'instructionComment' ||
    column.id === 'finalComment' ? (
    <OutlinedInput
      className="text-area-cell"
      onMouseLeave={() => {
        setTableCommentValue(commentValue, column.id);
      }}
      onMouseEnter={() => setShowmultiplerows(true)}
      onChange={(e) => {
        setCommentValue(e.target.value);
      }}
      value={commentValue || ''}
      placeholder={
        column.id === 'comment' ||
        column.id === 'instructionComment' ||
        column.id === 'finalComment'
          ? 'Input comment'
          : '@username'
      }
      disabled = {tableName === 'Completion Table'}
      inputProps={{ maxLength: 100 }}
      multiline={showmultiplerows ? true : false}
      style={{
        width: column.width,
        minWidth: column.width,
        fontSize: '14px',
        padding: '6px 10px',
        borderColor: '1px solid #DFDFDF',
        background: tableName === 'Completion Table' ? '#ddd' :  '#fff',
        height: showmultiplerows ? 'auto' : '32px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
      }}
    />
  ) : column.id === 'effectiveDate' || column.id === 'ExecDate' ? (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        slotProps={{ textField: { size: 'small' } }}
        value={row[column.id] ? dayjs(row[column.id]) : null}
        disabled= {tableName === 'Completion Table'}
        onChange={ tableName === 'Completion Table' ? null : (newValue) => handleDateChange(newValue, column.id)}
        renderInput={(params) => (
          <OutlinedInput
            {...params}
            sx={{
              width: column.width,
              minWidth: column.width,
              fontSize: '12px',
              color: 'white',
              borderColor: '1px solid #DFDFDF',
              background: '#fff',
              height: '32px',
              lineHeight: '32px',
              '& .MuiInputBase-input': {
                padding: '0',
                height: '32px',
                lineHeight: '32px'
              }
            }}
          />
        )}
      />
    </LocalizationProvider>
  ) : column.id === 'decision' ? (
    <OutlinedInput
      className="text-area-cell"
      onBlur={() => {
        setTableCommentValue(recommendationValue, column.id);
      }}
      onChange={(e) => {
        setRecommendationValue(e.target.value?.replace(/[^a-zA-Z0-9 ]/g, ''));
      }}
      value={recommendationValue || ''}
      inputProps={{
        maxLength: 24
      }}
      placeholder={'Input decision'}
      style={{
        width: column.width,
        minWidth: column.width,
        fontSize: '14px',
        padding: '6px 10px',
        borderColor: '1px solid #DFDFDF',
        background: '#fff',
        height: '32px'
      }}
    />
  ) : tableName === 'Completion Table' ? (
    <MyAutocompleteComponent
      userOptions={getUserDetails(tagDecisionUsers, row[column.id])}
      selectedOptions={
       getUserDetails(tagDecisionUsers, row[column.id])
      }
      width={'170px'}
      backgroundColor='#ddd'
    />
  ) : (
    <MyAutocompleteComponent
      userOptions={tableName === 'Decision Table' ? tagDecisionUsers : tagUsers}
      setSelectedOptions={setSelectedOptions}
      selectedOptions={
        tableName === 'Decision Table'
          ? getUserDetails(tagDecisionUsers, ownerEmail)
          : (taggedUsers && taggedUsers?.filter((item) => item.UserName !== null)) || []
      }
      setInputValue={setInputValue}
      inputValue={inputValue}
      setOpen={setOpen}
      handleInput={handleInput}
      handleChange={(e, newvalue) => handleChange(e, newvalue, CycleSKUKey)}
      handleClear={handleClear}
      width={'170px'}
    />
  );
};
